
import React, { useState } from 'react';
import { VideoLength, StoryStructure } from '../types';
import type { StoryInputFormProps } from '../types';

const lengthOptions = [
  { value: VideoLength.FULL_8S, label: '8-Second Story', segments: 1 },
  { value: VideoLength.FULL_16S, label: '16-Second Story', segments: 2 },
  { value: VideoLength.FULL_24S, label: '24-Second Story', segments: 3 },
];


const structureOptions = [
  { 
    value: StoryStructure.HOOK_TO_END, 
    label: 'Hook to End', 
    icon: '🎯',
    description: 'Strong hook that keeps viewers watching till the end'
  },
  { 
    value: StoryStructure.DRAMATIC_TWIST, 
    label: 'Dramatic Twist', 
    icon: '🔄',
    description: 'Story with an unexpected twist that shocks viewers'
  },
  { 
    value: StoryStructure.EMOTIONAL_JOURNEY, 
    label: 'Emotional Journey', 
    icon: '❤️',
    description: 'Heartwarming story that touches emotions'
  },
  { 
    value: StoryStructure.COMEDY_BUILDUP, 
    label: 'Comedy Buildup', 
    icon: '😂',
    description: 'Humorous structure with escalating comedy'
  },
  { 
    value: StoryStructure.MYSTERY_REVEAL, 
    label: 'Mystery Reveal', 
    icon: '🔍',
    description: 'Creates curiosity and provides the answer'
  },
  { 
    value: StoryStructure.TUTORIAL_STEPS, 
    label: 'Step-by-Step Tutorial', 
    icon: '📚',
    description: 'Easy to follow step-by-step instructions'
  },
  { 
    value: StoryStructure.BEFORE_AFTER, 
    label: 'Before/After', 
    icon: '✨',
    description: 'Shows dramatic transformation'
  },
  { 
    value: StoryStructure.CHALLENGE_RESULT, 
    label: 'Challenge & Result', 
    icon: '🏆',
    description: 'Shows challenge process and amazing results'
  },
  { 
    value: StoryStructure.FACTS_SURPRISE, 
    label: 'Surprising Facts', 
    icon: '💡',
    description: 'Reveals shocking and interesting facts'
  },
  { 
    value: StoryStructure.STORY_LOOP, 
    label: 'Loop Structure', 
    icon: '♾️',
    description: 'Story where the end connects to the beginning'
  },
];

const StoryInputForm: React.FC<StoryInputFormProps> = ({ onGenerate, isLoading }) => {
  const [topic, setTopic] = useState('');
  const [length, setLength] = useState<VideoLength>(VideoLength.FULL_16S);
  const [structure, setStructure] = useState<StoryStructure>(StoryStructure.HOOK_TO_END);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Prevent form from causing any layout shifts
    const button = e.currentTarget.querySelector('button[type="submit"]') as HTMLButtonElement;
    if (button) {
      button.blur();
    }
    
    if (topic.trim()) {
      // Use setTimeout to ensure UI doesn't jump
      setTimeout(() => {
        onGenerate(topic, length, structure);
      }, 0);
    }
  };

  return (
    <div className="bg-slate-800/60 p-6 rounded-2xl shadow-2xl border border-slate-700">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="topic" className="block text-lg font-medium text-slate-300 mb-2">
            Video Topic
          </label>
          <textarea
            id="topic"
            name="topic"
            rows={3}
            className="w-full bg-slate-900/70 border border-slate-600 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-slate-200 p-3 transition duration-200 placeholder:text-slate-500"
            placeholder="e.g., A funny cat video, tips for growing houseplants, a 1-minute travel guide to Tokyo..."
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            required
            disabled={isLoading}
          />
        </div>

        <div>
          <label className="block text-lg font-medium text-slate-300 mb-3">
            스토리 구성 방식
          </label>
          <div className="grid grid-cols-2 gap-3">
            {structureOptions.map((option) => (
              <label key={option.value} className="cursor-pointer">
                <input
                  type="radio"
                  name="structure"
                  value={option.value}
                  checked={structure === option.value}
                  onChange={() => setStructure(option.value)}
                  className="sr-only"
                  disabled={isLoading}
                />
                <div
                  className={`p-3 rounded-lg border-2 transition duration-200 ${
                    structure === option.value
                      ? 'bg-indigo-600/20 border-indigo-500 shadow-lg'
                      : 'bg-slate-700/50 border-slate-600 hover:bg-slate-600/50 hover:border-slate-500'
                  }`}
                >
                  <div className="flex items-center mb-1">
                    <span className="text-2xl mr-2">{option.icon}</span>
                    <span className={`font-semibold ${
                      structure === option.value ? 'text-indigo-300' : 'text-slate-200'
                    }`}>
                      {option.label}
                    </span>
                  </div>
                  <p className={`text-xs ${
                    structure === option.value ? 'text-indigo-200' : 'text-slate-400'
                  }`}>
                    {option.description}
                  </p>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-lg font-medium text-slate-300 mb-3">
            Video Length
          </label>
          <div className="flex space-x-4">
            {lengthOptions.map((option) => (
              <label key={option.value} className="flex-1">
                <input
                  type="radio"
                  name="length"
                  value={option.value}
                  checked={length === option.value}
                  onChange={() => setLength(option.value)}
                  className="sr-only"
                  disabled={isLoading}
                />
                <div
                  className={`cursor-pointer text-center p-3 rounded-lg border-2 transition duration-200 ${
                    length === option.value
                      ? 'bg-indigo-600 border-indigo-500 text-white font-bold shadow-lg'
                      : 'bg-slate-700 border-slate-600 hover:bg-slate-600/50 hover:border-slate-500'
                  }`}
                >
                  <div className="font-semibold">{option.label}</div>
                  <div className="text-xs mt-1 opacity-80">
                    {option.segments} {option.segments === 1 ? 'segment' : 'segments'}
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>


        <div>
          <button
            type="button"
            disabled={isLoading || !topic.trim()}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleSubmit(e as any);
            }}
            className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-lg text-base font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-600 disabled:cursor-not-allowed transition duration-300 ease-in-out transform hover:scale-105 disabled:scale-100"
          >
            {isLoading ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Generating Story...
              </>
            ) : (
              '✨ Generate AI Story'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default StoryInputForm;
